/**
 * Lazily loads the Razorpay Checkout SDK script and opens the payment modal.
 */

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(window.Razorpay);
      return;
    }
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Razorpay));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
    document.head.appendChild(script);
  });
}

/**
 * Opens Razorpay Checkout modal.
 *
 * @param {object} options
 * @param {string} options.keyId         - Razorpay key_id
 * @param {string} options.orderId       - Razorpay order_id
 * @param {number} options.amount        - Amount in paise
 * @param {string} options.currency      - e.g. "INR"
 * @param {string} options.name          - Merchant / app name
 * @param {string} options.description   - Payment description
 * @param {string} options.userEmail     - Pre-fill email
 * @param {string} options.userName      - Pre-fill name
 * @param {function} options.onSuccess   - Called with { razorpay_payment_id, razorpay_order_id, razorpay_signature }
 * @param {function} options.onDismiss   - Called when modal is closed without payment
 */
export async function openRazorpayCheckout({
  keyId,
  orderId,
  amount,
  currency = 'INR',
  name = 'SastraAstro',
  description = 'Wallet Recharge',
  userEmail = '',
  userName = '',
  onSuccess,
  onDismiss,
}) {
  const RazorpayClass = await loadRazorpayScript();

  const rzp = new RazorpayClass({
    key: keyId,
    amount,
    currency,
    name,
    description,
    order_id: orderId,
    prefill: { name: userName, email: userEmail },
    theme: { color: '#6c5ce7' },
    // Force UPI-only payment method
    method: {
      upi: true,
      card: false,
      netbanking: false,
      wallet: false,
      emi: false,
      paylater: false,
    },
    config: {
      display: {
        blocks: {
          upi: {
            name: 'Pay using UPI',
            instruments: [
              { method: 'upi', flows: ['collect', 'intent', 'qr'] },
            ],
          },
        },
        sequence: ['block.upi'],
        preferences: { show_default_blocks: false },
      },
    },
    handler: (response) => {
      if (onSuccess) onSuccess(response);
    },
    modal: {
      ondismiss: () => {
        if (onDismiss) onDismiss();
      },
    },
  });

  rzp.open();
}

/**
 * Generates a UUID-style idempotency key for wallet operations.
 */
export function generateIdempotencyKey(prefix = 'txn') {
  const rand = () => Math.random().toString(36).substring(2, 10);
  return `${prefix}_${rand()}${rand()}_${Date.now()}`;
}
